<template>
  <div>
    <h4>Basic details</h4>
    <sme-card large>
      <div class="card-container">
        <app-input
          :value="name"
          :validate-immediate="false"
          name="Award name"
          description="3-50 characters"
          rules="required|min:3|max:50"
          type="text"
          placeholder="Keep it casual and encouraging"
          class="m-0"
          @input="$emit('update:name', $event)"
        >
          <template #label>
            <div class="label">
              Award name
              <small>Give this award a clear, meaningful name.</small>
            </div>
          </template>
        </app-input>

        <hr />

        <h5 class="scope-heading">Program scope</h5>

        <multi-select
          id="divisions-select"
          :value="selectedDivisionIds"
          :options="divisionOptions"
          placeholder="Select who will receive the award"
          :select-all="true"
          select-all-text="All divisions"
          :empty-means-all="true"
          :require-explicit-all="requireExplicitScope"
          searchable
          :search-min-chars="0"
          search-placeholder="Search divisions..."
          class="m-0"
          @input="$emit('update:selectedDivisionIds', $event)"
        >
          <template #label>
            <div class="label">
              Divisions
              <small>Choose where this award can be used and who can see/earn it.</small>
            </div>
          </template>
        </multi-select>
        <small v-if="scopeError" class="text-danger">{{ scopeError }}</small>

        <div v-if="recognitionPayTypeScopingEnabled" class="pay-type-container">
          <div class="label">
            Pay type
            <small>Restrict this award to salaried or hourly paid staff. Leave blank for both.</small>
          </div>
          <app-input
            :value="enabledPayType"
            :validate-immediate="false"
            name="Pay type"
            type="select"
            :options="payTypeOptions"
            :undefined-allow="true"
            undefined-label="All pay types"
            class="m-0"
            @input="$emit('update:enabledPayType', $event)"
          />
        </div>

        <slot name="after-divisions" />
      </div>
    </sme-card>
  </div>
</template>

<script setup>
import AppInput from '@/components/AppInput.vue';
import MultiSelect from '@/components/MultiSelect.vue';
import SmeCard from '@/components/atoms/SmeCard.vue';
import useFeatureFlags from '@/composables/useFeatureFlags';
import { RECOGNITION_PAY_TYPE_OPTIONS } from '@/pages/recognition/constants';

const { recognitionPayTypeScopingEnabled } = useFeatureFlags();
const payTypeOptions = RECOGNITION_PAY_TYPE_OPTIONS;

defineProps({
  name: {
    type: String,
    default: '',
  },
  // null means the admin hasn't chosen a scope yet, as opposed to [] = entire company.
  selectedDivisionIds: {
    type: Array,
    default: null,
  },
  divisionOptions: {
    type: Array,
    default: () => [],
  },
  // Create forms opt in so scope has to be chosen (B2B-144); edit forms leave this off
  // because the award being edited always has an explicit scope already.
  requireExplicitScope: {
    type: Boolean,
    default: false,
  },
  enabledPayType: {
    type: String,
    default: undefined,
  },
  scopeError: {
    type: String,
    default: '',
  },
});

defineEmits(['update:name', 'update:selectedDivisionIds', 'update:enabledPayType']);
</script>

<style scoped lang="scss">
@import '@/pages/recognition/styles/award-form';

// Sized to match the field labels it introduces, not the h5 default. The card is a
// flex column with a 1.5rem gap, so the negative margin is what tightens the space
// between this heading and the first field.
.scope-heading {
  color: var(--palette-color-default);
  font-weight: 500;
  font-size: 0.875rem;
  margin-bottom: -1rem;
}

// .label carries no bottom margin; the surrounding container supplies the spacing,
// as it does for the other label-plus-control blocks on this form.
.pay-type-container {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
</style>
